import { useMemo } from 'react';
import { getText } from 'i18n/intl';
import { availabilityRelationsSelector } from 'slices/availabilityRelation.slice';
import { membershipsSelector } from 'slices/memberships.slice';
import type { ObjectManagerDirtyFields } from 'slices/object.slice';
import {
  selectObjectTypesMap,
  selectObjectTypesTree,
} from 'slices/objectTypes.slice';
import { selectedReservationTemplateMap } from 'slices/reservationTemplate.slice';
import { useAppSelector } from 'slices/store';
import { getMemberTypes, getOptionalRelatedTypes } from 'utils/associations';
import { getSubTypes } from 'utils/objectTypes';
import type { TTEObject } from '@timeedit/types/lib/types';
import { TEDrawerSection } from '@timeedit/ui-components';
import {
  Collapse,
  type CollapseProps,
  Flex,
} from '@timeedit/ui-components/antd';
import AvailabilityRelated from './AvailabilityRelated';
import { Membership } from './Membership';
import OptionalRelated from './OptionalRelated';
import Organizations from './Organizations';
import Relations from './Relations';

type AssociationsProps = {
  dirtyFields: ObjectManagerDirtyFields;
  object: TTEObject;
  onChange: (object: Partial<TTEObject>) => void;
};

export const Associations = ({
  onChange,
  object,
  dirtyFields,
}: AssociationsProps) => {
  const { types } = object;
  const memberships = useAppSelector(membershipsSelector);
  const availabilityRelations = useAppSelector(availabilityRelationsSelector);
  const typeTree = useAppSelector(selectObjectTypesTree);
  const objectTypesMap = useAppSelector(selectObjectTypesMap);
  const reservationTemplatesMap = useAppSelector(
    selectedReservationTemplateMap
  );

  const showMembershipSection = useMemo(
    () =>
      object.members.length > 0 ||
      getMemberTypes(object.types, memberships, objectTypesMap).length > 0,
    [object.members, object.types, memberships, objectTypesMap]
  );

  const showAvailabilityRelated = useMemo(
    () =>
      availabilityRelations.some(({ availabilityTypes = [] }) =>
        availabilityTypes
          .flatMap((t) => getSubTypes(Number(t.type), typeTree))
          .some((t) => types.includes(t))
      ),
    [types, availabilityRelations, typeTree]
  );

  const showOptionalRelated = useMemo(() => {
    return (
      object.optionalRelations.length > 0 ||
      getOptionalRelatedTypes(
        types,
        objectTypesMap,
        typeTree,
        reservationTemplatesMap
      ).length > 0
    );
  }, [
    types,
    object.optionalRelations,
    typeTree,
    objectTypesMap,
    reservationTemplatesMap,
  ]);

  const collapsibleAssociations = useMemo<CollapseProps['items']>(() => {
    // The relations section should always be shown
    const associations: CollapseProps['items'] = [
      {
        key: 'relations',
        label: getText(
          'object_manager.object_table.drawer.associations.relations.title'
        ),
        children: (
          <Relations
            object={object}
            onChange={onChange}
            dirtyFields={dirtyFields}
          />
        ),
      },
    ];

    if (showMembershipSection) {
      associations.push({
        key: 'memberships',
        label: getText(
          'object_manager.object_table.drawer.associations.memberships.title'
        ),
        children: (
          <Membership
            object={object}
            changedIds={dirtyFields?.members}
            onChange={onChange}
          />
        ),
      });
    }

    if (showAvailabilityRelated) {
      associations.push({
        key: 'availability-related',
        label: getText(
          'object_manager.object_table.drawer.associations.availability_related.title'
        ),
        children: (
          <AvailabilityRelated
            object={object}
            onChange={onChange}
            dirtyFields={dirtyFields}
          />
        ),
      });
    }

    if (showOptionalRelated) {
      associations.push({
        key: 'optional-related',
        label: getText(
          'object_manager.object_table.drawer.associations.optional_related.title'
        ),
        children: (
          <OptionalRelated
            object={object}
            onChange={onChange}
            dirtyFields={dirtyFields}
          />
        ),
      });
    }

    return associations;
  }, [
    object,
    onChange,
    showMembershipSection,
    showAvailabilityRelated,
    showOptionalRelated,
  ]);

  return (
    <TEDrawerSection
      label={getText('object_manager.object_table.drawer.associations.title')}
    >
      <Flex vertical className="tw:gap-3">
        <Collapse items={collapsibleAssociations} accordion />
        <Organizations object={object} onChange={onChange} />
      </Flex>
    </TEDrawerSection>
  );
};
